/**
 * ActivityHistory — web port of
 * apps/WunderGuide/components/settingsDrawer/ActivityHistory.tsx.
 *
 * Features mirrored from RN:
 *  • Collapsible top-level toggle (chevron up/down)
 *  • Per-row expansion to reveal `source_id` (Reference)
 *  • Pagination via prev/next chevron pair
 *  • Expiry badge: "Expires on <date>" or pill "Expired"
 *  • Platform-specific labels: Apple App Store / Google Play Store / Promo
 *    Code / Admin Credit / Daily Quota
 *  • Backend grant-config for icons (optional — falls back to local map)
 */
import React, { useMemo, useState } from 'react';
import type { TransactionsResponse } from '../../api/billingApi';
import { formatTimeRemaining } from '../../types/allowance';
import { useLanguage } from '../../hooks/useLanguage';
import './ActivityHistory.css';

type Transaction = TransactionsResponse['transactions'][number];

export interface GrantTypeIconConfig {
  name: string;
  color: string;
}

export interface GrantTypeConfig {
  icon: GrantTypeIconConfig;
}

interface Props {
  transactions: Transaction[];
  loading?: boolean;
  /** Backend-driven icon/colour overrides keyed by source_type. Optional. */
  grantTypeConfigs?: Map<string, GrantTypeConfig>;
  /** Initial collapsed state. RN defaults closed. */
  initiallyOpen?: boolean;
  pageSize?: number;
}

const ActivityHistory: React.FC<Props> = ({
  transactions,
  loading = false,
  grantTypeConfigs,
  initiallyOpen = false,
  pageSize = 10,
}) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(initiallyOpen);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(transactions.length / pageSize));
  const pageItems = useMemo(
    () => transactions.slice(page * pageSize, page * pageSize + pageSize),
    [transactions, page, pageSize],
  );

  const toggleRow = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="wg-activity">
      <button
        type="button"
        className="wg-activity__toggle"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <ClockIcon />
        <span className="wg-activity__toggle-label">
          {t('ACTIVITY_HISTORY')}
        </span>
        <span
          className={`wg-activity__chev ${open ? 'is-up' : 'is-down'}`}
          aria-hidden
        >
          ▾
        </span>
      </button>

      {open && (
        <div className="wg-activity__container">
          {loading ? (
            <div className="wg-activity__empty">{t('LOADING')}</div>
          ) : transactions.length === 0 ? (
            <div className="wg-activity__empty">{t('NO_ACTIVITY')}</div>
          ) : (
            <>
              {pageItems.map(tx => (
                <Row
                  key={tx.id}
                  tx={tx}
                  expanded={expanded.has(tx.id)}
                  onToggle={() => toggleRow(tx.id)}
                  grantTypeConfigs={grantTypeConfigs}
                />
              ))}

              {totalPages > 1 && (
                <div className="wg-activity__pager">
                  <button
                    type="button"
                    className="wg-activity__pager-btn"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    aria-label={t('PREVIOUS_PAGE')}
                  >
                    ‹
                  </button>
                  <span className="wg-activity__pager-info">
                    {page + 1} / {totalPages}
                  </span>
                  <button
                    type="button"
                    className="wg-activity__pager-btn"
                    onClick={() =>
                      setPage(p => Math.min(totalPages - 1, p + 1))
                    }
                    disabled={page >= totalPages - 1}
                    aria-label={t('NEXT_PAGE')}
                  >
                    ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

interface RowProps {
  tx: Transaction;
  expanded: boolean;
  onToggle: () => void;
  grantTypeConfigs?: Map<string, GrantTypeConfig>;
}

const Row: React.FC<RowProps> = ({
  tx,
  expanded,
  onToggle,
  grantTypeConfigs,
}) => {
  const { t, selectedLanguage } = useLanguage();
  const { icon, details } = getTransactionDetails(tx, t, grantTypeConfigs);
  const expiresAt = tx.expires_at ? new Date(tx.expires_at) : null;
  const isExpired = !!expiresAt && expiresAt < new Date();
  const isCredit = tx.quantity >= 0;

  // Mirrors RN: use description if present, else humanize source_type. For
  // session rows (negative debits), prefer the localized "Voice Session"
  // label since description is typically empty.
  const title = tx.description
    ? tx.description
    : tx.source_type.startsWith('session')
      ? t('VOICE_SESSION')
      : tx.source_type
          .replace(/_/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase());

  return (
    <button
      type="button"
      className="wg-activity__row"
      onClick={onToggle}
      aria-expanded={expanded}
    >
      <div className="wg-activity__row-head">
        <span className="wg-activity__row-icon">{icon}</span>
        <span className="wg-activity__row-title">{title}</span>
        <span
          className={`wg-activity__row-amount${
            isExpired ? ' is-expired' : isCredit ? ' is-pos' : ' is-neg'
          }`}
        >
          {isCredit ? '+' : '−'}
          {formatTimeRemaining(Math.abs(tx.quantity))}
        </span>
      </div>

      <div className="wg-activity__row-meta">
        {details && (
          <>
            <span>{details}</span>
            <span className="wg-activity__row-meta-sep">•</span>
          </>
        )}
        <span>{new Date(tx.occurred_at).toLocaleString(selectedLanguage)}</span>
      </div>

      {expiresAt && (
        <div className="wg-activity__row-expiry">
          {isExpired ? (
            <span className="wg-activity__row-expired-pill">
              {t('EXPIRED')}
            </span>
          ) : (
            <>
              {t('EXPIRES_ON')}{' '}
              <span>{expiresAt.toLocaleDateString(selectedLanguage)}</span>
            </>
          )}
        </div>
      )}

      {expanded && (
        <div className="wg-activity__row-expanded">
          <span className="wg-activity__row-expanded-label">
            {t('REFERENCE')}:
          </span>
          <span className="wg-activity__row-expanded-value">
            {tx.source_id}
          </span>
        </div>
      )}
    </button>
  );
};

function getTransactionDetails(
  tx: Transaction,
  t: (key: string) => string,
  grantTypeConfigs?: Map<string, GrantTypeConfig>,
): { icon: React.ReactNode; details: string | null } {
  const sourceType = tx.source_type;
  const meta = tx.metadata as Record<string, unknown> | undefined;
  const platform = (meta?.platform || meta?.store) as string | undefined;
  const code = (meta?.code || meta?.promo_code) as string | undefined;

  let details: string | null = null;
  if (sourceType === 'iap_purchase' || sourceType === 'iap') {
    if (platform === 'apple' || platform === 'ios')
      details = t('APPLE_APP_STORE');
    else if (platform === 'google' || platform === 'android')
      details = t('GOOGLE_PLAY_STORE');
    else details = t('IN_APP_PURCHASE');
  } else if (
    sourceType === 'code_redemption' ||
    sourceType === 'promo_code' ||
    sourceType === 'promo_grant'
  ) {
    details = code ? `${t('CODE')}: ${code}` : t('PROMO_CODE');
  } else if (sourceType === 'admin_grant' || sourceType === 'manual_grant') {
    details = t('ADMIN_CREDIT');
  } else if (sourceType === 'quota_grant' || sourceType === 'daily_quota') {
    details = t('DAILY_QUOTA');
  }

  // Backend-driven icon override is optional. If not present we render a
  // local SVG. (RN's icon library uses Ionicons by name; web replaces with
  // small inline SVGs to avoid an icon font.)
  const fromConfig = grantTypeConfigs?.get(sourceType);
  if (fromConfig) {
    return {
      icon: <span style={{ color: fromConfig.icon.color }}>●</span>,
      details,
    };
  }

  switch (sourceType) {
    case 'iap_purchase':
    case 'iap':
      return { icon: <BagIcon />, details };
    case 'iap_manual_recovery':
      return { icon: <RefreshIcon />, details };
    case 'promo_code':
    case 'code_redemption':
      return { icon: <TicketIcon />, details };
    case 'promo_grant':
      return { icon: <GiftIcon />, details };
    case 'admin_grant':
    case 'manual_grant':
      return { icon: <ShieldIcon />, details };
    case 'quota_grant':
    case 'daily_quota':
      return { icon: <ClockIcon />, details };
    case 'session_usage':
    case 'session':
    case 'session_realtime':
    case 'session_final':
      return { icon: <ChatIcon />, details };
    default:
      return { icon: <DotIcon />, details };
  }
}

const baseSvgProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const ClockIcon = () => (
  <svg {...baseSvgProps}>
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);
const BagIcon = () => (
  <svg {...baseSvgProps}>
    <path d="M6 7h12l-1 13H7L6 7z" />
    <path d="M9 7V5a3 3 0 0 1 6 0v2" />
  </svg>
);
const RefreshIcon = () => (
  <svg {...baseSvgProps}>
    <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
    <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
  </svg>
);
const TicketIcon = () => (
  <svg {...baseSvgProps}>
    <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V9z" />
  </svg>
);
const GiftIcon = () => (
  <svg {...baseSvgProps}>
    <rect x="3" y="8" width="18" height="13" rx="2" />
    <line x1="12" y1="8" x2="12" y2="21" />
  </svg>
);
const ShieldIcon = () => (
  <svg {...baseSvgProps}>
    <path d="M12 3l8 4v5c0 5-4 9-8 9s-8-4-8-9V7l8-4z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);
const ChatIcon = () => (
  <svg {...baseSvgProps}>
    <path d="M21 12a8 8 0 0 1-13 6.3L3 21l1.7-5A8 8 0 1 1 21 12z" />
  </svg>
);
const DotIcon = () => (
  <svg {...baseSvgProps}>
    <circle cx="12" cy="12" r="3" fill="currentColor" />
  </svg>
);

export default ActivityHistory;
